import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { UseFormReturn } from 'react-hook-form'
import { Address, PublicClient, WalletClient } from 'viem'
import { SwapFormSchema } from './validators'

export interface SwapFormNormalizedData {
  from: TokenWithValue
  to: TokenWithValue
}

export type SwapMode = 'swap' | 'wrap' | 'unwrap'

export interface UseDebouncedFormValuesArgs {
  form: UseFormReturn<SwapFormSchema>
  swappableAssets: TokenWithBalance[]
}

export interface UseDebouncedDialogFormValuesResult {
  debouncedFormValues: SwapFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}

export interface SwapPairConfig {
  from: TokenSymbol | string
  to: TokenSymbol | string
}

// LiFi Quote Response Types (must be defined before BaseResult)
export interface LifiToken {
  address: string
  decimals: number
  symbol: string
  chainId: number
  name: string
  priceUSD?: string
}

export interface LifiEstimate {
  tool: string
  fromAmount: string
  fromAmountUSD?: string
  toAmount: string
  toAmountMin: string
  toAmountUSD?: string
  approvalAddress: string
  feeCosts?: Array<{
    name: string
    amount: string
    amountUSD?: string
    token: LifiToken
  }>
  gasCosts?: Array<{
    type: string
    amount: string
    amountUSD?: string
    token: LifiToken
  }>
}

export interface LifiAction {
  fromChainId: number
  fromAmount: string
  fromToken: LifiToken
  toChainId: number
  toToken: LifiToken
  slippage: number
  fromAddress: string
  toAddress: string
}

export interface LifiTransactionRequest {
  from: Address
  to: Address
  chainId: number
  data: `0x${string}`
  value: string
  gasPrice?: string
  gasLimit?: string
}

export interface LifiQuoteResponse {
  id: string
  type: 'swap' | 'cross' | 'lifi' | 'protocol'
  tool: string
  action: LifiAction
  estimate: LifiEstimate
  transactionRequest: LifiTransactionRequest
}

export interface BaseResult {
  inputToken: Address
  outputToken: Address
  feeToken: Address
  outputReceiver: Address
  inputSender: Address
  inputAmount: string
  outputAmount: string
  partnerFee: string
  routingFee: string
  effectiveOutputAmount: string
  minOutputAmount: string
  liquidityModules: string[]
  router: Address
  inputAmountUSD?: string | null
  outputAmountUSD?: string | null
  effectiveInputAmount?: string | null
  effectiveOutputAmountUSD?: string | null
  effectiveInputAmountUSD?: string | null
}

export interface GetPriceResult {
  statusCode: number
  result: BaseResult
}

export interface GetQuoteResult {
  statusCode: number
  result: BaseResult & {
    calldata: `0x${string}`
    router: Address
  }
}

export interface GetPriceError {
  statusCode: number
  error: string
}

export interface UseSwapFormFieldsArgs {
  form: UseFormReturn<SwapFormSchema>
  assets: TokenWithBalance[]
  fieldName: 'from' | 'to'
  counterpartFieldName: 'to' | 'from'
  marketInfo: MarketInfo
  isOnSwapPage: boolean
  filterZeroBalance: boolean
  toTokens?: Token[]
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  resetErrors: () => void
}

export interface UseSwapFormFieldsResults {
  selectedAsset: TokenWithBalance
  availableAssets: TokenWithBalance[]
  changeToken: (newSymbol: TokenSymbol) => void
  maxSelectedFieldName: string | undefined
  maxValue: NormalizedUnitNumber
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSetMax: ((max: NormalizedUnitNumber) => void) | undefined
}

export class SwapError extends Error {
  public details?: Record<string, unknown>

  constructor(message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'SwapError'
    this.details = details
  }
}

export class MissingQuoteError extends SwapError {}

/**
 * Adapts a LiFi quote response to the existing GetPriceResult format.
 * This allows the rest of the codebase to work unchanged.
 *
 * IMPORTANT: We use the original input/output token addresses (passed to the API)
 * rather than what LiFi returns. This is necessary because:
 * - useTrade.tsx validates using hypeAddressOverride (converts 0xEeee... to 0x2222... WHYPE)
 * - LiFi returns 0x0000... for native tokens
 * - Using original addresses ensures validation passes
 */
export function adaptLifiQuoteToGetPriceResult(
  lifiQuote: LifiQuoteResponse,
  fromAddress: Address,
  originalInputToken: Address,
  originalOutputToken: Address,
): GetPriceResult {
  const { action, estimate, transactionRequest } = lifiQuote

  // Calculate fees from LiFi fee costs (use BigInt to avoid precision loss with wei values)
  const totalFeeCosts = estimate.feeCosts?.reduce((sum, fee) => sum + BigInt(fee.amount || '0'), 0n) ?? 0n

  return {
    statusCode: 200,
    result: {
      // Use original addresses for validation compatibility with hypeAddressOverride
      inputToken: originalInputToken,
      outputToken: originalOutputToken,
      feeToken: action.fromToken.address as Address,
      outputReceiver: fromAddress,
      inputSender: fromAddress,
      inputAmount: estimate.fromAmount,
      outputAmount: estimate.toAmount,
      partnerFee: '0', // LiFi doesn't have a separate partner fee
      routingFee: totalFeeCosts.toString(),
      effectiveOutputAmount: estimate.toAmount,
      minOutputAmount: estimate.toAmountMin,
      liquidityModules: [lifiQuote.tool],
      router: transactionRequest.to,
      inputAmountUSD: estimate.fromAmountUSD ?? null,
      outputAmountUSD: estimate.toAmountUSD ?? null,
      effectiveInputAmount: estimate.fromAmount,
      effectiveOutputAmountUSD: estimate.toAmountUSD ?? null,
      effectiveInputAmountUSD: estimate.fromAmountUSD ?? null,
    },
  }
}

export interface WalletConfig {
  publicClient: PublicClient | undefined
  walletClient: WalletClient | undefined
  account: Address | undefined
}
